/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { CloudAccount, UpdateCloudAccountInput } from "~/generated/graphql";
import { CloudAccountFields } from "./types";

export function toCloudAccountPutInput(
  account: CloudAccount
): UpdateCloudAccountInput {
  return {
    id: account.id,
    name: account.name,
    description: account.description,
    environment: account.environment,
    regions: account.regions,
    metadata: account.metadata?.map((pair) => ({
      key: pair.key,
      value: pair.value,
    })),
  };
}

export function getUpdateObject(
  data: CloudAccountFields,
  cloudAccount: CloudAccount
): UpdateCloudAccountInput {
  const putCloudAccount = toCloudAccountPutInput(cloudAccount);
  const credentialsUpdate = getCredentialsUpdateObject(data, cloudAccount);
  if (credentialsUpdate !== undefined) {
    return {
      ...putCloudAccount,
      credentials: credentialsUpdate,
    };
  }

  return { ...putCloudAccount, ...data };
}

function getCredentialsUpdateObject(
  data: CloudAccountFields,
  cloudAccount: CloudAccount
): UpdateCloudAccountInput["credentials"] | undefined {
  const credentialsBuilders: Partial<
    Record<
      CloudAccount["provider"],
      (
        fields: CloudAccountFields
      ) => UpdateCloudAccountInput["credentials"] | undefined
    >
  > = {
    AWS: getAwsCredentialsUpdate,
    AZURE: getAzureCredentialsUpdate,
    GC: getGcCredentialsUpdate,
    IBM: getIbmCredentialsUpdate,
  };

  return credentialsBuilders[cloudAccount.provider]?.(data);
}

function getAwsCredentialsUpdate(
  data: CloudAccountFields
): UpdateCloudAccountInput["credentials"] | undefined {
  if (data.arn === undefined || data.externalId === undefined) {
    return undefined;
  }

  return {
    aws: {
      rolesChain: [
        {
          externalId: data.externalId,
          arn: data.arn ?? "",
        },
      ],
    },
  };
}

function getAzureCredentialsUpdate(
  data: CloudAccountFields
): UpdateCloudAccountInput["credentials"] | undefined {
  if (
    data.appId === undefined ||
    data.password === undefined ||
    data.tenantId === undefined
  ) {
    return undefined;
  }

  return {
    azure: {
      appId: data.appId ?? "",
      password: data.password ?? "",
      tenantId: data.tenantId ?? "",
    },
  };
}

function getGcCredentialsUpdate(
  data: CloudAccountFields
): UpdateCloudAccountInput["credentials"] | undefined {
  if (data.keyFile === undefined) {
    return undefined;
  }

  return {
    gc: {
      keyFile: data.keyFile ?? "",
    },
  };
}

function getIbmCredentialsUpdate(
  data: CloudAccountFields
): UpdateCloudAccountInput["credentials"] | undefined {
  if (data.apiKey === undefined) {
    return undefined;
  }

  return {
    ibm: {
      apiKey: data.apiKey ?? "",
    },
  };
}
